import { useCallback } from "react";
import InspectIcon from "@assets/icons/new/inspect_24px.svg?react";
import { FaroButton } from "@components/common/faro-button";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { NewBadge } from "@components/common/new-badge";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { assert } from "@faro-lotv/foundation";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { WorkflowState } from "@pages/data-management/data-management-types";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props extends BaseProjectIdProps {
  /** Workflow state of the project */
  state: WorkflowState;
  /** Flag if we're coming from the error view and should have a text button instead of an icon button. */
  isFromErrorView: boolean;
  /** Flag if the button is only visible because of dev mode. */
  isDevMode: boolean;
  /** Flag to show the badge on the inspect button. */
  hasBadRegistration?: boolean;
  /** ID of the registration revision. */
  registrationId?: string;
  /** Optional callback when the button is clicked, called before the main action. */
  onClickBefore?: () => void;
}

/** Button to inspect the (bad) registration result. */
export function InspectButton({
  projectId,
  state,
  isFromErrorView,
  isDevMode,
  registrationId,
  hasBadRegistration,
  onClickBefore,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { navigateToInspectAndPublishTool } = useAppNavigation();

  const onAdjustRegistration = useCallback((): void => {
    assert(registrationId, "Registration ID should be known at this point.");

    if (onClickBefore) {
      onClickBefore();
    }

    trackEvent({
      name: DataManagementEvents.inspect,
      props: {
        projectId,
        registrationId,
      },
    });

    navigateToInspectAndPublishTool({
      projectId: projectId,
      registrationId: registrationId,
    });
  }, [navigateToInspectAndPublishTool, onClickBefore, projectId, registrationId, trackEvent]);

  const isDisabled = !registrationId && !isDevMode;

  let tooltip;
  if (isDisabled) {
    tooltip = "Loading data...";
  } else if (state === "registerBadResult") {
    tooltip = "Inspect the scans to adjust the registration.";
  } else if (state === "published") {
    tooltip = "Inspect the published scans.";
  } else {
    tooltip = "Inspect the registration results.";
  }

  let element: JSX.Element;
  if (isFromErrorView) {
    element = (
      <FaroButton
        dataTestId="sa-button-inspect-text"
        isDisabled={isDisabled}
        onClick={onAdjustRegistration}
      >
        Adjust Registration
      </FaroButton>
    );
  } else {
    const iconButton = (
      <FaroIconButton
        dataTestId="sa-button-inspect-icon"
        component={InspectIcon}
        isDisabled={isDisabled}
        onClick={onAdjustRegistration}
      />
    );

    if (state === "registerBadResult" || hasBadRegistration) {
      element = (
        <NewBadge sx={{
          display: "initial",
          marginRight: "10px",
        }}>
          {iconButton}
        </NewBadge>
      );
    } else {
      element = iconButton;
    }
  }

  return (
    <SphereTooltip
      title={ tooltip }
      enterDelay={state === "registerBadResult" || hasBadRegistration ? 0 : undefined}
    >
      { element }
    </SphereTooltip>
  );
}
